/**
 * O cronômetro da chamada — isolado num componente PRÓPRIO de propósito.
 *
 * **Por que isto existe (07/08/2026).** O contador vivia dentro do `CallPane`,
 * o mesmo componente que hospedava a sala WebRTC:
 *
 * ```tsx
 * const [nowMs, setNowMs] = useState(() => Date.now());
 * useEffect(() => {
 *   const id = setInterval(() => setNowMs(Date.now()), 1000);  // ← a cada segundo
 * }, []);
 * ...
 * <InlineCallRoom token={...} onDisconnected={onEnd} onErro={setErro} />
 * ```
 *
 * Cada tique re-renderizava o `<InlineCallRoom>`. As funções passadas a ele
 * eram criadas na renderização, então ganhavam **identidade nova** a cada
 * segundo; o `useLiveKitRoom` via as dependências mudarem e refazia a conexão.
 *
 * O resultado, medido no servidor LiveKit em produção: o participante entrava,
 * publicava áudio e vídeo em 720p — e **saía sozinho 13 segundos depois**, três
 * vezes seguidas, com `CLIENT_REQUEST_LEAVE`. No console, quinze
 * `already connected to room` seguidos de `NegotiationError: negotiation timed
 * out`. A enxurrada de `connect()` concorrentes quebrava a negociação SDP.
 *
 * Eu havia classificado esses `already connected` como ruído inofensivo. Não
 * eram: eram a causa.
 *
 * **A regra que fica:** num componente que hospeda conexão persistente
 * (WebRTC, WebSocket, SSE), estado que muda por TEMPO precisa morar noutro
 * componente. Aqui o `setInterval` re-renderiza 40 caracteres de texto, e mais
 * nada.
 */
import { useEffect, useState } from "react";

/** `mm:ss`, ou `hh:mm:ss` quando passa de uma hora. Veio do `AtendimentosPage`
 *  junto com o cronômetro — só ele usa. */
export function formatCallDuration(startedAt: string, nowMs: number): string {
  const startMs = new Date(startedAt).getTime();
  const totalSec = Math.max(0, Math.floor((nowMs - startMs) / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  if (h > 0) return `${pad(h)}:${pad(m)}:${pad(s)}`;
  return `${pad(m)}:${pad(s)}`;
}

export interface DuracaoDaChamadaProps {
  /** ISO 8601 de quando a chamada começou. */
  startedAt: string;
  className?: string;
}

export function DuracaoDaChamada({ startedAt, className }: DuracaoDaChamadaProps) {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <span className={className} data-testid="call-duration">
      {formatCallDuration(startedAt, nowMs)}
    </span>
  );
}
